import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { PageHero } from '@/components/shared/PageHero';
import { SERVICES } from '@/data/mockData';
import { generateReference, storage } from '@/lib/storage';
import {
  ArrowRight, ArrowLeft, Check, FileText, User, MapPin, Sparkles, Copy,
  ChevronRight, Clipboard,
} from 'lucide-react';
import { toast } from 'sonner';

const PUROKS = ['Purok 1', 'Purok 2', 'Purok 3', 'Purok 4', 'Purok 5', 'Purok 6', 'Purok 7'];
const STEPS = ['Service', 'Personal Info', 'Address', 'Review'];

const RequestDocument = () => {
  const [params] = useSearchParams();
  const preselected = params.get('type');

  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    type: preselected || '',
    purpose: '',
    name: '',
    email: '',
    contact: '',
    age: '',
    civil: '',
    purok: '',
    street: '',
    yearsOfStay: '',
    consent: false,
  });
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    if (preselected) setStep((s) => Math.max(s, 1));
  }, [preselected]);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const stepIsValid = () => {
    if (step === 0) return Boolean(data.type && data.purpose);
    if (step === 1) return Boolean(data.name && data.email && data.contact && data.age);
    if (step === 2) return Boolean(data.purok && data.street);
    if (step === 3) return data.consent;
    return true;
  };

  const handleSubmit = () => {
    if (!data.consent) return toast.error('Please agree to the data privacy notice.');
    const ref = generateReference('R');
    const record = {
      id: ref,
      type: data.type,
      purpose: data.purpose,
      name: data.name,
      email: data.email,
      contact: data.contact,
      age: data.age,
      civil: data.civil,
      address: `${data.street}, ${data.purok}`,
      purok: data.purok,
      street: data.street,
      yearsOfStay: data.yearsOfStay,
      status: 'Submitted',
      date: new Date().toISOString().slice(0, 10),
      submittedAt: new Date().toISOString(),
      timeline: [
        { status: 'Submitted', date: new Date().toISOString(), note: 'Online submission received.' },
      ],
    };
    storage.push('requests', record);
    setSubmitted(record);
    toast.success('Request submitted successfully!', { description: `Reference: ${ref}` });
  };

  if (submitted) return <SuccessScreen record={submitted} />;

  return (
    <div data-testid="request-document-page">
      <PageHero
        eyebrow="E-Services"
        title="Request a Document"
        subtitle="Fill out the form to request a barangay document. Pickup at the barangay hall once approved."
        breadcrumbs={[{ label: 'E-Services', to: '/e-services' }, { label: 'Request a Document' }]}
      />

      <section className="container-app section-padding">
        {/* Stepper */}
        <div className="mx-auto max-w-3xl flex items-center justify-between mb-10">
          {STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <div className="flex flex-col items-center text-center">
                <div className={`h-10 w-10 rounded-full inline-flex items-center justify-center font-semibold text-sm transition ${
                  i < step ? 'bg-success text-success-foreground' : i === step ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'
                }`}>
                  {i < step ? <Check className="h-4 w-4" /> : i + 1}
                </div>
                <div className={`mt-1.5 text-[11px] font-medium ${i === step ? 'text-primary' : 'text-muted-foreground'}`}>{label}</div>
              </div>
              {i < STEPS.length - 1 && <div className={`h-0.5 flex-1 mx-2 ${i < step ? 'bg-success' : 'bg-border'}`} />}
            </React.Fragment>
          ))}
        </div>

        <Card className="max-w-3xl mx-auto p-6 sm:p-8 border-0 shadow-md-soft">
          {step === 0 && (
            <div className="space-y-5" data-testid="step-service">
              <h2 className="font-heading text-xl font-semibold inline-flex items-center gap-2"><FileText className="h-5 w-5 text-primary" /> Choose Service</h2>
              <div>
                <Label>Service / Document Type *</Label>
                <Select value={data.type} onValueChange={(v) => update('type', v)}>
                  <SelectTrigger data-testid="req-type"><SelectValue placeholder="Select a document" /></SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.id} value={s.title}>{s.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Purpose of Request *</Label>
                <Textarea
                  value={data.purpose}
                  onChange={(e) => update('purpose', e.target.value)}
                  placeholder="e.g., Employment, scholarship, business renewal..."
                  rows={3}
                  data-testid="req-purpose"
                />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5" data-testid="step-personal">
              <h2 className="font-heading text-xl font-semibold inline-flex items-center gap-2"><User className="h-5 w-5 text-primary" /> Personal Information</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2"><Label>Full Name *</Label><Input value={data.name} onChange={(e) => update('name', e.target.value)} placeholder="Juan Dela Cruz" data-testid="req-name" /></div>
                <div><Label>Email *</Label><Input type="email" value={data.email} onChange={(e) => update('email', e.target.value)} placeholder="you@email.com" data-testid="req-email" /></div>
                <div><Label>Mobile Number *</Label><Input value={data.contact} onChange={(e) => update('contact', e.target.value)} placeholder="0917-XXX-XXXX" data-testid="req-contact" /></div>
                <div><Label>Age *</Label><Input type="number" value={data.age} onChange={(e) => update('age', e.target.value)} placeholder="30" data-testid="req-age" /></div>
                <div>
                  <Label>Civil Status</Label>
                  <Select value={data.civil} onValueChange={(v) => update('civil', v)}>
                    <SelectTrigger data-testid="req-civil"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Single">Single</SelectItem>
                      <SelectItem value="Married">Married</SelectItem>
                      <SelectItem value="Widowed">Widowed</SelectItem>
                      <SelectItem value="Separated">Separated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="step-address">
              <h2 className="font-heading text-xl font-semibold inline-flex items-center gap-2"><MapPin className="h-5 w-5 text-primary" /> Address Details</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Purok *</Label>
                  <Select value={data.purok} onValueChange={(v) => update('purok', v)}>
                    <SelectTrigger data-testid="req-purok"><SelectValue placeholder="Select purok" /></SelectTrigger>
                    <SelectContent>
                      {PUROKS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>House No. & Street *</Label><Input value={data.street} onChange={(e) => update('street', e.target.value)} placeholder="123 Sampaguita St." data-testid="req-street" /></div>
                <div><Label>Years of Stay</Label><Input value={data.yearsOfStay} onChange={(e) => update('yearsOfStay', e.target.value)} placeholder="e.g., 12" data-testid="req-years" /></div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="step-review">
              <h2 className="font-heading text-xl font-semibold inline-flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" /> Review & Submit</h2>
              <Card className="p-5 bg-secondary/40 border">
                <ReviewRow label="Service" value={data.type} />
                <ReviewRow label="Purpose" value={data.purpose} />
                <ReviewRow label="Name" value={data.name} />
                <ReviewRow label="Email" value={data.email} />
                <ReviewRow label="Mobile" value={data.contact} />
                <ReviewRow label="Age / Civil" value={`${data.age || '-'} · ${data.civil || '-'}`} />
                <ReviewRow label="Address" value={`${data.street}, ${data.purok}`} />
              </Card>
              <label className="flex items-start gap-3 cursor-pointer">
                <Checkbox checked={data.consent} onCheckedChange={(v) => update('consent', Boolean(v))} data-testid="req-consent" className="mt-0.5" />
                <span className="text-sm text-muted-foreground">
                  I agree that my information will be used by Brgy San Isidro for processing this request, in accordance with the Data Privacy Act of 2012.
                </span>
              </label>
            </div>
          )}

          <div className="mt-8 flex justify-between">
            <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => s - 1)} data-testid="req-prev">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
            {step < STEPS.length - 1 ? (
              <Button disabled={!stepIsValid()} onClick={() => setStep((s) => s + 1)} className="bg-gradient-primary text-primary-foreground" data-testid="req-next">
                Continue <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button disabled={!stepIsValid()} onClick={handleSubmit} className="bg-gradient-primary text-primary-foreground" data-testid="req-submit">
                <Check className="mr-2 h-4 w-4" /> Submit Request
              </Button>
            )}
          </div>
        </Card>
      </section>
    </div>
  );
};

const ReviewRow = ({ label, value }) => (
  <div className="flex items-start gap-3 py-1.5 text-sm">
    <span className="w-32 text-muted-foreground flex-shrink-0">{label}</span>
    <span className="font-medium flex-1">{value || '—'}</span>
  </div>
);

const SuccessScreen = ({ record }) => {
  const copy = () => {
    navigator.clipboard.writeText(record.id);
    toast.success('Reference number copied');
  };
  return (
    <div data-testid="request-success-page">
      <PageHero
        eyebrow="Submitted"
        title="Your request has been received!"
        subtitle="Save your reference number — you can use it to track the status of this request anytime."
        breadcrumbs={[{ label: 'E-Services', to: '/e-services' }, { label: 'Submitted' }]}
      />
      <section className="container-app -mt-12 relative z-10 max-w-2xl mx-auto">
        <Card className="p-8 border-0 shadow-elegant text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-success/15 flex items-center justify-center">
            <Check className="h-7 w-7 text-success" />
          </div>
          <h3 className="mt-4 font-display text-2xl font-bold">Reference Number</h3>
          <div className="mt-3 inline-flex items-center gap-2 rounded-xl bg-secondary px-5 py-3 font-mono text-lg font-bold text-primary-deep" data-testid="success-ref">
            {record.id}
            <Button size="icon" variant="ghost" onClick={copy} className="h-8 w-8" data-testid="success-copy">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-5 text-sm text-muted-foreground">
            We've recorded your request for <strong>{record.type}</strong>. Please bring a valid ID when picking up your document.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <Button asChild variant="outline" data-testid="success-track">
              <Link to={`/track-request?ref=${record.id}`}>
                <Clipboard className="mr-2 h-4 w-4" /> Track this request
              </Link>
            </Button>
            <Button asChild className="bg-gradient-primary text-primary-foreground" data-testid="success-home">
              <Link to="/">Back to home <ArrowRight className="ml-2 h-4 w-4" /></Link>
            </Button>
          </div>
        </Card>
      </section>
    </div>
  );
};

export default RequestDocument;
